import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import { ApiFetch } from '../App';

export interface AssessmentAnalysisData {
  probabilita_performance?: string;
  performance_percepita?: string;
  warnings: string[];
  norme: Record<string, string>;
  medie_chiave: Record<string, string>;
  voce_bedrock: string;
  surveyId: string;
  [key: string]: any; // fallback for unexpected keys
}

interface AssessmentAnalysisProps {
  analysis: AssessmentAnalysisData | null;
  apiFetch: ApiFetch;
}

const NORM_KEY_MAP: Record<string, string> = {
  Conoscersi: 'report.norm_conoscersi',
  Costruire: 'report.norm_costruire',
  Discutere: 'report.norm_discutere',
  Proattività: 'report.norm_proattivita',
  Contesto: 'report.norm_contesto',
  Connettersi: 'report.norm_connettersi',
};

const MEDIE_KEY_MAP: Record<string, string> = {
  Fondamentali: 'analysis.medie_fondamentali',
  'Capitale sociale': 'analysis.medie_capitale_sociale',
  Crescita: 'analysis.medie_crescita',
  Risultati: 'analysis.medie_risultati',
  Sostenibilità: 'analysis.medie_sostenibilita',
  Velocità: 'analysis.medie_velocita',
};

const WARNING_KEY_MAP: Record<string, string> = {
  Frustrazione: 'analysis.warning_frustrazione',
  'Capacità di problem solving': 'analysis.warning_problem_solving',
  Stanchezza: 'analysis.warning_stanchezza',
  'Eccessiva zona di comfort': 'analysis.warning_comfort',
  'Orientamento tattico': 'analysis.warning_tattico',
  'Difficoltà nella messa a terra': 'analysis.warning_messa_a_terra',
  Frenesia: 'analysis.warning_frenesia',
  'Eccessiva tranquillità': 'analysis.warning_tranquillita',
  'Scarso apprendimento': 'analysis.warning_apprendimento',
  'Scarsa capacità realizzativa': 'analysis.warning_realizzativa',
  'Rischi di turnover': 'analysis.warning_turnover',
  'Scarsa spinta': 'analysis.warning_spinta',
};

const COLOR_KEY_MAP: Record<string, string> = {
  rosso: 'analysis.color_rosso',
  giallo: 'analysis.color_giallo',
  verde: 'analysis.color_verde',
};

export const AssessmentAnalysis: React.FC<AssessmentAnalysisProps> = ({ analysis, apiFetch }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [loadingPdf, setLoadingPdf] = useState(false);

  const translateColor = (value: string): string =>
    value.replace(/^(rosso|giallo|verde)/, (c) => t(COLOR_KEY_MAP[c] ?? c));

  const translateNormKey = (key: string): string =>
    NORM_KEY_MAP[key] ? t(NORM_KEY_MAP[key]) : key;

  const translateMedieKey = (key: string): string =>
    MEDIE_KEY_MAP[key] ? t(MEDIE_KEY_MAP[key]) : key;

  const translateWarning = (w: string): string => (WARNING_KEY_MAP[w] ? t(WARNING_KEY_MAP[w]) : w);

  const downloadPpt = async () => {
    setLoading(true);
    try {
      const response = await apiFetch({
        surveyId: analysis!.surveyId.toString(),
        operation: 'download_ppt',
      });

      if (response.ok) {
        let jsonResponse = await response.json();
        const a = document.createElement('a');
        a.href = jsonResponse['output'];
        a.download = '';
        document.body.appendChild(a);
        a.click();
        a.remove();

        setLoading(false);
      } else {
        console.error(response);
        setLoading(false);
      }
    } catch (error) {
      console.error('An error occurred:', error);
      setLoading(false);
    }
  };

  const downloadPdf = async () => {
    setLoadingPdf(true);
    try {
      const response = await apiFetch({
        surveyId: analysis!.surveyId.toString(),
        operation: 'download_pdf',
      });

      if (response.ok) {
        let jsonResponse = await response.json();
        window.open(jsonResponse['output'], '_blank');
        setLoadingPdf(false);
      } else {
        console.error(response);
        setLoadingPdf(false);
      }
    } catch (error) {
      console.error('An error occurred:', error);
      setLoadingPdf(false);
    }
  };

  return (
    analysis &&
    (analysis['voce_bedrock'] === 'Survey del tipo sbagliato' ? (
      <Typography variant="body1" padding={4}>
        {t('analysis.wrong_survey_type')}
      </Typography>
    ) : (
      <Box paddingX={4} style={{ textAlign: 'left' }}>
        <Typography variant="body2" paddingBottom={2}>
          <b>{t('analysis.probabilita_performance')}</b>:{' '}
          {translateColor(analysis['probabilita_performance'] ?? '')}
        </Typography>
        <Typography variant="body2" paddingBottom={2}>
          <b>{t('analysis.performance_percepita')}</b>:{' '}
          {translateColor(analysis['performance_percepita'] ?? '')}
        </Typography>
        {analysis['warnings'].length > 0 && (
          <Typography variant="body2" paddingBottom={2}>
            <b>{t('analysis.warnings')}</b>: {analysis['warnings'].map(translateWarning).join(', ')}
          </Typography>
        )}
        <Grid
          spacing={6}
          paddingBottom={2}
          container
          direction="row"
          sx={{ justifyContent: 'flex-start', alignItems: 'center' }}
        >
          <Grid>
            <Typography variant="body2">
              <b>{t('analysis.norme')}</b>
            </Typography>
            {Object.keys(analysis['norme']).map((n) => (
              <Typography variant="body2">
                {translateNormKey(n)}: {translateColor(analysis['norme'][n])}
              </Typography>
            ))}
          </Grid>
          <Grid>
            <Typography variant="body2">
              <b>{t('analysis.medie')}</b>
            </Typography>
            {Object.keys(analysis['medie_chiave']).map((n) => (
              <Typography variant="body2">
                {translateMedieKey(n)}: {analysis['medie_chiave'][n]}
              </Typography>
            ))}
          </Grid>
        </Grid>
        <Typography variant="body2">
          <b>{t('analysis.temi')}</b>
        </Typography>
        {analysis['voce_bedrock']
          .replaceAll('THEME: ', 'TEMA:')
          .replaceAll('|', ', ')
          .split('TEMA:')
          .filter((t) => t !== '')
          .map((t) => (
            <Typography variant="body2" paddingBottom={2}>
              {t}
            </Typography>
          ))}
        <Button loading={loading} variant="contained" sx={{ mt: 3 }} onClick={() => downloadPpt()}>
          {t('analysis.download_ppt')}
        </Button>
        <Button
          loading={loadingPdf}
          variant="contained"
          sx={{ mt: 3, ml: 2 }}
          onClick={() => downloadPdf()}
        >
          {t('analysis.download_pdf')}
        </Button>
        <Button
          variant="outlined"
          sx={{ mt: 3, ml: 2 }}
          onClick={() => window.open(`/report/${analysis!.surveyId}`, '_blank')}
        >
          {t('analysis.see_report')}
        </Button>
      </Box>
    ))
  );
};
